import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  AvatarGroup,
} from '@commenda/kakashi';
import { TWorkflowExecution } from '@commenda/types';
import {
  ServiceRequestDetails,
  TaskGroupWithSLA,
} from '@commenda/types/src/service-request/types';
import { cn } from '@commenda/utils';
import { ServiceRequestModel } from '@commenda/utils/models/service-request';
import { TaskParty } from '@prisma/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { KeyedMutator } from 'swr';
import { getStatusIcon, getStepGroupDescription } from '../../../../utils';
import { ServiceRequestStepSLABadge } from './ServiceRequestStepSLABadge';
import { ServiceRequestTaskCountBadge } from './ServiceRequestTaskCountBadge';
import { ServiceRequestTimelineTaskGroup } from './ServiceRequestTimelineTaskGroup';
import { WorkflowStepDescription } from '../../../../../components/WorkflowStepDescription';

type TServiceRequestTimelineProps = {
  serviceRequest: ServiceRequestDetails;
  taskGroups: TaskGroupWithSLA[];
  role: TaskParty;
  mutate: KeyedMutator<unknown>;
  workflowExecution?: TWorkflowExecution;
};

const TimelineAvatars = ({
  serviceRequest,
  taskGroup,
}: {
  serviceRequest: ServiceRequestDetails;
  taskGroup: TaskGroupWithSLA;
}) => {
  const pendingParties = new Set(
    taskGroup.tasks.map((task) => task.pendingOn)
  );
  const hasAgentTasks = pendingParties.has(TaskParty.AGENT);

  // Roll up the per-task assignees across the group's CLIENT tasks. Dedup by
  // user id, fall back to the first SR-level assignee for any task that hasn't
  // been individually assigned yet.
  const clientAssignees = (() => {
    const byId = new Map<string, string>(); // id -> name
    let sawUnassigned = false;
    for (const task of taskGroup.tasks) {
      if (task.pendingOn !== TaskParty.CLIENT) continue;
      const assigned = task.AssignedUser;
      if (assigned?.id && assigned.name) {
        byId.set(assigned.id, assigned.name);
      } else {
        sawUnassigned = true;
      }
    }
    if (byId.size === 0 && sawUnassigned) {
      const fallback =
        serviceRequest?.ServiceRequestAssignees?.[0]?.User?.name;
      // No id for the SR-level fallback — seed by name.
      if (fallback) return [{ id: fallback, name: fallback }];
    }
    return Array.from(byId, ([id, name]) => ({ id, name }));
  })();

  const MAX_AVATARS = 3;

  return (
    <div className="flex items-center pr-2">
      {clientAssignees.length > 0 && (
        <AvatarGroup
          size="xs"
          limit={MAX_AVATARS}
          avatars={clientAssignees.map(({ id, name }) => ({
            name,
            colorSeed: id,
            tooltip: { content: name },
          }))}
        />
      )}
      {hasAgentTasks && (
        <div
          className={cn(
            'size-6 rounded-full bg-white border border-border-secondary flex items-center justify-center overflow-hidden',
            clientAssignees.length > 0 && '-ml-2'
          )}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src="/commenda-new-logo.svg"
            alt="Commenda"
            className="size-4 object-contain"
          />
        </div>
      )}
    </div>
  );
};

export const ServiceRequestTimeline = ({
  serviceRequest,
  taskGroups,
  role,
  mutate,
  workflowExecution,
}: TServiceRequestTimelineProps) => {
  const currentTaskGroups = useMemo(() => {
    return ServiceRequestModel.getCurrentTaskGroups(taskGroups);
  }, [taskGroups]);

  const areAllTasksCompleted = useMemo(() => {
    return ServiceRequestModel.isAllTasksCompleted(serviceRequest);
  }, [serviceRequest]);

  const [openItems, setOpenItems] = useState<string[]>(
    areAllTasksCompleted
      ? []
      : currentTaskGroups
        ? currentTaskGroups.map((group) => group.id.toString())
        : []
  );

  const handleValueChange = (values: string[] | string) => {
    if (typeof values === 'string') {
      setOpenItems([values]);
    } else {
      setOpenItems(values);
    }
  };

  const wasAllCompleted = useRef(areAllTasksCompleted);

  useEffect(() => {
    if (areAllTasksCompleted) {
      // Collapse once when the SR finishes; later refetches must not close
      // groups the user expanded — modals live inside the task cards.
      if (!wasAllCompleted.current) setOpenItems([]);
      return;
    }

    if (currentTaskGroups) {
      setOpenItems(currentTaskGroups.map((group) => group.id.toString()));
    }
  }, [currentTaskGroups, areAllTasksCompleted]);

  useEffect(() => {
    wasAllCompleted.current = areAllTasksCompleted;
  }, [areAllTasksCompleted]);

  return (
    <div className="relative">
      <Accordion
        type="multiple"
        defaultValue={openItems}
        value={openItems}
        onValueChange={handleValueChange}
        className="border-none"
      >
        <div className="flex flex-col justify-center">
          {taskGroups.map((taskGroup, index) => {
            const shouldShowTaskCountBadge =
              ServiceRequestModel.shouldShowTaskCountBadge(
                taskGroup,
                currentTaskGroups,
                role,
                serviceRequest
              );

            return (
              <AccordionItem
                key={taskGroup.id}
                value={taskGroup.id.toString()}
                className="bg-bg-primary border-none"
              >
                <div className="flex flex-col">
                  <AccordionTrigger className="px-xs hover:no-underline min-h-16 py-4 border-b">
                    <div className="flex items-center gap-3xl w-full">
                      <div className="flex items-center gap-lg w-full pr-3xl">
                        <div className="shrink-0">
                          {getStatusIcon(taskGroup, taskGroups)}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="flex items-center gap-md">
                            <div className="txt-large-bold text-text-primary">
                              {taskGroup.title}
                            </div>
                            <ServiceRequestStepSLABadge
                              workflowDefinitionStepGroupId={
                                taskGroup.workflowDefinitionStepGroupId
                              }
                              workflowExecution={workflowExecution}
                            />
                          </div>
                          <WorkflowStepDescription
                            description={getStepGroupDescription(
                              taskGroup,
                              workflowExecution
                            )}
                            className="txt-small-regular text-text-secondary mt-0.5"
                          />
                        </div>
                        {shouldShowTaskCountBadge && (
                          <div className="flex items-center gap-[10px] shrink-0">
                            <ServiceRequestTaskCountBadge
                              taskGroup={taskGroup}
                              role={role}
                            />
                          </div>
                        )}
                        <TimelineAvatars
                          serviceRequest={serviceRequest}
                          taskGroup={taskGroup}
                        />
                      </div>
                    </div>
                  </AccordionTrigger>

                  <AccordionContent>
                    <ServiceRequestTimelineTaskGroup
                      serviceRequest={serviceRequest}
                      taskGroup={taskGroup}
                      role={role}
                      mutate={mutate}
                      workflowExecution={workflowExecution}
                    />
                  </AccordionContent>
                </div>
              </AccordionItem>
            );
          })}
        </div>
      </Accordion>
    </div>
  );
};
